/* global chrome */
import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { clearSessionDialog, isSessionDialogActive } from "../Utils";
import { useHistory } from "react-router-dom";
import { Box, Button, Paper, Typography } from "@mui/material";
import { Stack } from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import { hasAzureSSOAccess, isDemo } from "../App";

const Login = () => {
  const [nikoError, setNikoError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [timeoutSeconds, setTimeoutSeconds] = useState(15);
  const MAX_RETRIES = 2;
  const INITIAL_RETRY_DELAY = 1000;
  const EXTENSION_ID = "dljjgckappionimanmfejhbkkacjledj";

  const [isAwaitingNikoAuth, setIsAwaitingNikoAuth] = useState(false);

  const LoadingIndicator = ({ message, progress }) => (
    <Box className="flex items-center space-x-3">
      {/* Primary spinner */}
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />

      {/* Message */}
      <span>{message}</span>

      {/* Progress percentage if provided */}
      {progress && (
        <span className="text-sm text-gray-600">({Math.round(progress)}%)</span>
      )}

      {/* Material UI CircularProgress */}
      <CircularProgress
        size={16}
        sx={{
          color: "#00AB4E", // Matching the blue color used in the original code
        }}
      />
    </Box>
  );

  const handleError = (error, context) => {
    const errorMessages = {
      extension:
        "Unable to connect to NikoHealth extension. Please ensure you only have the NikoHealth platform open and are signed in. Close all other .nikohealth.com tabs and try again.",
      extensionTimeout:
        "Connection is taking longer than expected. Please ensure you only have the NikoHealth platform open and are signed in. Close all other .nikohealth.com tabs and try again.",
      nikoAuth:
        "NikoHealth authentication failed. Please verify you're logged into NikoHealth.",
      celeritas: "Unable to connect to Celeritas. Our team has been notified.",
      network:
        "Network connection issue detected. Please check your internet connection.",
    };

    const supportInfo = "";

    setNikoError(`${errorMessages[context] || error.message} ${supportInfo}`);
    setIsLoading(false);
  };

  const setSessionToken = (token) => {
    try {
      // Validate token format
      if (!token || typeof token !== "string") {
        throw new Error("Invalid token format");
      }

      // Check storage availability
      const testKey = "__storage_test__";
      sessionStorage.setItem(testKey, testKey);
      sessionStorage.removeItem(testKey);

      // Store token
      sessionStorage.setItem("token", token);
      return true;
    } catch (error) {
      console.error("Storage error:", error);
      if (
        error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED"
      ) {
        handleError(
          new Error(
            "Browser storage is full. Please clear some space and try again.",
          ),
          "storage",
        );
      } else if (error.name === "SecurityError") {
        handleError(
          new Error(
            "Unable to store login data. Please ensure cookies are enabled.",
          ),
          "storage",
        );
      } else {
        handleError(error, "storage");
      }
      return false;
    }
  };

  const checkExtension = () => {
    if (!window.chrome || !chrome.runtime) {
      setNikoError(
        <span>
          Please install the Celeritas Elsa Chrome extension to continue.
          <a
            href={`https://chrome.google.com/webstore/detail/${EXTENSION_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 ml-1"
          >
            Click here to install
          </a>
          . After installing, refresh this page.
        </span>,
      );
      setIsLoading(false);
      return false;
    }
    return true;
  };

  const loginwithAzureAd = () => {
    if (window?.location?.href) {
      let url = `${process.env.REACT_APP_BaseUrl?.split("/api")[0]}`;
      window?.location?.replace(url);
    }
  };

  useEffect(() => {
    if (isSessionDialogActive()) {
      clearSessionDialog();
    }
  });

  React.useEffect(() => {
    const isFromNiko = window.opener && window.location.href.includes("login");

    if (isFromNiko) {
      setIsAwaitingNikoAuth(true);
      setIsLoading(true);
      setLoadingMessage("Waiting for NikoHealth authentication ");
    }
  }, []);

  // Enhanced timeout handling
  useEffect(() => {
    let intervalId;
    let timeoutId;

    if (isAwaitingNikoAuth) {
      setTimeoutSeconds(15);

      intervalId = setInterval(() => {
        setTimeoutSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      timeoutId = setTimeout(() => {
        if (isAwaitingNikoAuth) {
          setIsAwaitingNikoAuth(false);
          setIsLoading(false);
          handleError(new Error("Authentication timed out"), "nikoAuth");
        }
      }, 15000);
    }

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isAwaitingNikoAuth]);

  const handleNikoAuth = async (
    token,
    givenName,
    surname,
    email,
    attempt = 0,
  ) => {
    const MAX_AUTH_RETRIES = 2;
    const AUTH_RETRY_DELAY = 1000; // 1 second

    try {
      setIsLoading(true);
      setLoadingMessage(
        `Authenticating with Celeritas${
          attempt > 0 ? ` (Attempt ${attempt + 1}/${MAX_AUTH_RETRIES + 1})` : ""
        } `,
      );
      setNikoError("");

      const authData = {
        accessToken: token,
        firstName: givenName,
        lastName: surname,
        email,
      };

      // Add a small initial delay on retries
      if (attempt > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, AUTH_RETRY_DELAY * attempt),
        );
      }

      const response = await fetch(
        `${process.env.REACT_APP_BaseUrl?.split("/api")[0]}/niko-auth`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(authData),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.redirectUrl) {
        setLoadingMessage("Authentication successful. Redirecting ");
        window.location.href = data.redirectUrl;
      }
    } catch (error) {
      console.error("Auth error (attempt " + (attempt + 1) + "):", error);

      // If we haven't exceeded max retries, try again
      if (attempt < MAX_AUTH_RETRIES) {
        setLoadingMessage(
          `Retrying authentication in ${AUTH_RETRY_DELAY / 1000} seconds `,
        );
        return handleNikoAuth(token, givenName, surname, email, attempt + 1);
      }

      handleError(error, "celeritas");
    } finally {
      // Don't set loading to false if we're redirecting
      if (!window.location.href.includes("redirectUrl")) {
        setIsLoading(false);
        setLoadingMessage("");
      }
    }
  };

  const requestNikoTokenWithRetry = async (
    attempt = 0,
    delay = INITIAL_RETRY_DELAY,
  ) => {
    setNikoError(""); // Clear any existing errors
    setIsLoading(true);
    setLoadingMessage(
      `Connecting to NikoHealth extension${
        attempt > 0 ? ` (Attempt ${attempt + 1}/${MAX_RETRIES + 1})` : ""
      } `,
    );

    if (!checkExtension()) {
      return;
    }

    try {
      await new Promise((resolve, reject) => {
        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { type: "REQUEST_NIKO_AUTH" },
          (response) => {
            if (chrome.runtime.lastError) {
              reject(new Error(chrome.runtime.lastError.message));
              return;
            }

            if (response?.authenticated && response?.token) {
              resolve(response);
              handleNikoAuth(
                response?.celeritasToken,
                response?.givenName,
                response?.surname,
                response?.email,
                0, // Initial attempt
              );
            } else {
              reject(new Error("Authentication failed or invalid response"));
            }
          },
        );
      });
    } catch (error) {
      console.error(`Attempt ${attempt + 1} failed:`, error);

      if (attempt < MAX_RETRIES) {
        setLoadingMessage(`Retrying in ${delay / 1000} seconds `);

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Retry with exponential backoff
        return requestNikoTokenWithRetry(
          attempt + 1,
          Math.min(delay * 2, 2000), // Cap maximum delay at 2 seconds
        );
      } else {
        const isTimeoutIssue =
          error.message.includes("timeout") ||
          error.message.includes("failed to respond") ||
          attempt >= MAX_RETRIES;

        handleError(error, isTimeoutIssue ? "extensionTimeout" : "extension");
      }
    }
  };

  // Replace the old requestNikoToken with the new retry version
  const requestNikoToken = () => {
    setRetryCount(0);
    requestNikoTokenWithRetry();
  };

  React.useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data.type === "NIKO_AUTH") {
        try {
          setIsAwaitingNikoAuth(false);
          setIsLoading(true);
          setLoadingMessage("Processing NikoHealth authentication ");
          setNikoError("");

          const { token, givenName, surname, email } = event.data;
          console.log("Received auth data:", {
            token,
            givenName,
            surname,
            email,
          });

          if (setSessionToken(token)) {
            await handleNikoAuth(token, givenName, surname, email, 0);
          }
        } catch (error) {
          console.error("Error processing Niko auth:", error);
          handleError(error, "nikoAuth");
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <div
      className="fixed inset-0 justify-center overflow-hidden bg-white"
      style={{
        paddingRight: "4rem",
        paddingLeft: "5rem",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* SVG */}
      <div
        className="w-1/2 relative"
        style={{
          marginRight: "6rem",
        }}
      >
        <img
          style={{
            height: "100vh",
          }}
          src="/loginPageImage.svg"
          alt="Geometric pattern"
          className="absolute inset-0 w-full object-cover"
        />
      </div>

      {/* Login Details */}
      <div>
        {/* Header */}
        <div
          style={{
            fontSize: "1.75rem",
            fontWeight: "bold",
            color: "#00AB4E",
            display: "inline-block",
            paddingRight: "1rem",
          }}
        >
          Intelligent Intake by Celeritas AI
        </div>

        {/* Description */}
        <div
          style={{
            marginTop: "1.5rem",
          }}
        >
          <div
            style={{
              fontSize: "1.15rem",
              fontWeight: "600",
            }}
          >
            Welcome! To continue, please log in to your company's
          </div>
          <div
            style={{
              fontSize: "1.15rem",
              fontWeight: "600",
            }}
          >
            Niko Health. Once logged in, return here to proceed.
          </div>
        </div>

        {/* Login Button */}
        <div
          style={{
            display: "flex",
            marginTop: "1.5rem",
            justifyContent: "left",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={requestNikoToken}
            disabled={isLoading}
            sx={{
              backgroundColor: isLoading ? "#9DC2E8" : "#00AB4E",
              "&:hover": {
                backgroundColor: isLoading ? "#9DC2E8" : "#008C40", // Darker green for hover
              },
              "& .MuiButton-startIcon": {
                marginRight: "8px",
              },
            }}
          >
            {isLoading ? (
              <LoadingIndicator
                message={loadingMessage}
                progress={
                  isAwaitingNikoAuth ? ((15 - timeoutSeconds) / 15) * 100 : null
                }
              />
            ) : (
              hasAzureSSOAccess ? "Continue with NikoHealth" : "CONTINUE"
            )}
          </Button>

          <div
            style={{
              paddingLeft: "1.5rem",
              maxWidth: "400px",
            }}
          >
            <span
              style={{
                fontSize: "0.875rem",
                color: "#E11D48",
                fontWeight: "600",
                wordWrap: "break-word",
                display: "inline-block",
              }}
            >
              {nikoError}
            </span>
          </div>
        </div>

        {hasAzureSSOAccess && <>
        <Typography variant="h6">
          OR
        </Typography>

        {/* Azure SSO Login */}
        <Button variant="contained" onClick={loginwithAzureAd}>
            Azure SSO
        </Button>
        </>}

        {/* Terms of Use */}
        <div
          style={{
            marginTop: "1.5rem",
          }}
        >
          <div
            style={{
              fontSize: "0.95rem",
              fontWeight: "600",
              color: "#595959",
            }}
          >
            By continuing, you acknowledge and accept our Terms of Use,
          </div>
          <div
            style={{
              fontSize: "0.95rem",
              fontWeight: "600",
              color: "#595959",
            }}
          >
            Privacy Policy, and Security Guidelines.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
